import { faqs } from '../data/faq'
import { currency, heroImage, price } from '../data/copy'

const TITLE =
  'MEGAPACK Educación Física 360 — 120 sesiones listas para el patio'
const DESCRIPTION =
  'Sistema 6 en 1 para docentes de Educación Física: 120 sesiones completas, 50+ juegos sin material, evaluación por competencias y plan anual de 36 semanas. Acceso inmediato en PDF.'
const SITE_NAME = 'MEGAPACK Educación Física 360'

const FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Bitter:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap'

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content: string,
): void {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attr, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertLink(
  rel: string,
  href: string,
  extra?: Record<string, string>,
): HTMLLinkElement {
  let tag = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"][href="${href}"]`,
  )
  if (!tag) {
    tag = document.createElement('link')
    tag.rel = rel
    tag.href = href
    document.head.appendChild(tag)
  }
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => tag!.setAttribute(key, value))
  }
  return tag
}

function upsertJsonLd(id: string, data: unknown): void {
  let tag = document.getElementById(id) as HTMLScriptElement | null
  if (!tag) {
    tag = document.createElement('script')
    tag.id = id
    tag.type = 'application/ld+json'
    document.head.appendChild(tag)
  }
  tag.textContent = JSON.stringify(data)
}

/**
 * SEO + carga de recursos críticos.
 *
 * Las tipografías se piden de forma asíncrona (no bloquean el primer pintado)
 * y la imagen del encabezado se precarga, que es el mayor elemento visible.
 */
export function applyHead(): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = 'es-AR'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large')
  upsertMeta('name', 'theme-color', '#14603A')
  upsertMeta(
    'name',
    'viewport',
    'width=device-width, initial-scale=1, viewport-fit=cover',
  )

  upsertMeta('property', 'og:type', 'product')
  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:image', heroImage)
  upsertMeta('property', 'og:locale', 'es_AR')
  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', heroImage)

  if (typeof window !== 'undefined') {
    const canonical = `${window.location.origin}${window.location.pathname}`
    upsertLink('canonical', canonical)
    upsertMeta('property', 'og:url', canonical)
  }

  // Recursos críticos: precarga del LCP y conexión anticipada a las fuentes.
  upsertLink('preload', heroImage, { as: 'image', fetchpriority: 'high' })
  upsertLink('preconnect', 'https://fonts.googleapis.com')
  upsertLink('preconnect', 'https://fonts.gstatic.com', { crossorigin: '' })

  // Las fuentes se aplican una vez descargadas: nunca bloquean el render.
  const fontLink = upsertLink('stylesheet', FONTS_HREF, { media: 'print' })
  fontLink.addEventListener(
    'load',
    () => {
      fontLink.media = 'all'
    },
    { once: true },
  )

  upsertJsonLd('ld-product', {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: SITE_NAME,
    description: DESCRIPTION,
    image: heroImage,
    brand: { '@type': 'Brand', name: SITE_NAME },
    offers: {
      '@type': 'Offer',
      price: price.replace(/[^0-9]/g, ''),
      priceCurrency: currency,
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '2500',
    },
  })

  upsertJsonLd('ld-faq', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  })
}
